import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AuthentifiedPage from "./AuthentifiedPage";
import AddDocumentMenuWrapper from "../components/AddDocumentMenuWrapper";
import CollecteLoadingMessage from "../components/CollecteLoadingMessage";
import { Domaine, SousDomaine, SousSousDomaine } from "../types/Domaine";
import { useAuth } from "../context/AuthContext";
import { Indicateur } from "../types/Indicateur";
import { rapportService } from "../services/RapportService";
import { Rapport } from "../types/Rapport";

const RapportModelesPage: React.FC = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();

  const [modeles, setModeles] = useState<Rapport[]>([]);
  const [query, setQuery] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const [buttonClicked, setButtonClicked] = useState<"createIndicator" | "createRapport" | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchModeles = async () => {
      if (!api) return;

      setLoading(true);
      try {
        const data = await rapportService.getAllRapports(api);

        const dataAvecIndicateurs = data.map((r: Rapport) => {
          const indicateurs: Indicateur[] = (r.rapportIndicateurs ?? [])
            .map((ri) => ri.indicateur)
            .filter((i): i is Indicateur => !!i);

          return {
            ...r,
            indicateurs,
          };
        });

        setModeles(dataAvecIndicateurs);

      } catch (error) {
        console.error("Erreur lors du chargement des modèles :", error);
      } finally {
        setLoading(false);
      }
    };

    fetchModeles();
  }, [api]);

  const filtered = useMemo(() => {
    if (!query) return modeles;
    return modeles.filter((m) =>
      (m.titre ?? "").toLowerCase().includes(query.toLowerCase()) ||
      (m.description ?? "").toLowerCase().includes(query.toLowerCase())
    );
  }, [query, modeles]);

  // Pagination
  const pageSize = 10;
  const [page, setPage] = useState(1);
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageData = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleCreateRapport = (domain: Domaine, sousDomain?: SousDomaine, sousSousDomain?: SousSousDomaine) => {
    setShowMenu(false);
    setLoading(true);
    const safeDomain = { ...domain, icon: undefined };
    const safeSousDomain = sousDomain ? { ...sousDomain, icon: undefined } : undefined;
    const safeSousSousDomain = sousSousDomain ? { ...sousSousDomain, icon: undefined } : undefined;
    setTimeout(() => {
      setLoading(false);
      navigate("/create-rapport", {
        state: {
          domain: safeDomain,
          sousDomain: safeSousDomain,
          sousSousDomain: safeSousSousDomain,
          mode: "createRapport",
        },
      });
    }, 500);
  };

  const handleCreateIndicator = (domain: Domaine, sousDomain?: SousDomaine, sousSousDomain?: SousSousDomaine) => {
    setShowMenu(false);
    setLoading(true);
    const safeDomain = { ...domain, icon: undefined };
    const safeSousDomain = sousDomain ? { ...sousDomain, icon: undefined } : undefined;
    const safeSousSousDomain = sousSousDomain ? { ...sousSousDomain, icon: undefined } : undefined;
    setTimeout(() => {
      setLoading(false);
      navigate("/create-indicateur", {
        state: {
          domain: safeDomain,
          sousDomain: safeSousDomain,
          sousSousDomain: safeSousSousDomain,
          mode: "createIndicator",
        },
      });
    }, 500);
  };

  const openModele = (modele: Rapport) => {
    navigate("/rapport-from-modele", { state: { modele } });
  };

  return (
    <AuthentifiedPage>
      <main className="container py-4">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h4 className="text-success fw-bold mb-0">Modèles de rapports</h4>
          <div className="button-group">
            {user?.role === "Admin" && (
              <button className="btn-import" onClick={() => { setButtonClicked("createRapport"); setShowMenu(true); }}>
                <i className="bi bi-file-earmark-text"></i> Créer un rapport
              </button>
            )}
            {user?.role === "Admin" && (
              <button className="btn-import" onClick={() => { setButtonClicked("createIndicator"); setShowMenu(true); }}>
                <i className="bi bi-bar-chart-line"></i> Créer un indicateur
              </button>
            )}
          </div>
        </div>

        <div className="filters-bar mb-3">
          <div className="input-wrapper">
            <span className="search-icon"><i className="bi bi-search"></i></span>
            <input type="text" className="search-input" placeholder="Rechercher..." value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
        </div>

        {showMenu && (
          <AddDocumentMenuWrapper
            onCreateIndicator={handleCreateIndicator}
            onCreateRapport={handleCreateRapport}
            onClose={() => setShowMenu(false)}
            action={buttonClicked}
            onSelect={() => {}}
            onCreateForm={() => {}}
          />
        )}

        {loading && <CollecteLoadingMessage message="Chargement..." />}

        {filtered.length === 0 ? (
          <p>Aucun modèle enregistré pour le moment.</p>
        ) : (
          <div className="table-responsive shadow-sm">
            <table className="table table-hover align-middle">
              <thead className="table-success">
                <tr>
                  <th>ID</th>
                  <th>Fenêtre</th>
                  <th>Sous Fenêtre</th>
                  <th>Description</th>
                  <th>Indicateurs</th>
                  <th>Date création</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {pageData.map((modele) => {
                  const fenetre =
                    modele.indicateurs?.[0]?.nomFenetre ?? "Non définie";

                  return (
                    <tr
                      key={modele.idRapport}
                      style={{ cursor: "pointer" }}
                      onClick={() => openModele(modele)}
                    >
                      <td>{modele.idRapport}</td>


                      <td>{fenetre}</td>
                      <td>{fenetre}</td>

                      <td>{modele.description ?? "Aucune"}</td>

                      <td>
                        {(modele.indicateurs ?? [])
                          .map((ind) => ind.nom)
                          .join(", ")}
                      </td>

                      <td>
                        {new Date(modele.createdAt).toLocaleString("fr-FR")}
                      </td>

                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            openModele(modele);
                          }}
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
          <small className="text-muted">{pageData.length} modèles affichés sur {total}</small>
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage(page - 1)}>
                  <i className="bi bi-chevron-left"></i>
                </button>
              </li>
              {pageNumbers.map((num) => (
                <li key={num} className={`page-item ${num === currentPage ? "active" : ""}`}>
                  <button className="page-link" onClick={() => setPage(num)}>{num}</button>
                </li>
              ))}
              <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage(page + 1)}>
                  <i className="bi bi-chevron-right"></i>
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </main>
    </AuthentifiedPage>
  );
};

export default RapportModelesPage;